import axiosInstance, { getAccessToken } from './axiosConfig';
import { requireAuthToken } from '../utils/authUtils';

const customerAPI = {
  getAll: async (params = {}) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.get('/customers', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch customers';
    }
  },

  getById: async (id) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.get(`/customers/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch customer';
    }
  },

  create: async (data) => {
    try {
      requireAuthToken(getAccessToken);
      console.log('[customerAPI.create] posting:', data);
      const response = await axiosInstance.post('/customers', data);
      console.log('[customerAPI.create] response status:', response.status, 'data:', response.data);
      return response.data;
    } catch (error) {
      console.error('[customerAPI.create] error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to create customer');
    }
  },

  update: async (id, data) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.put(`/customers/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update customer');
    }
  },

  delete: async (id) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.delete(`/customers/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete customer';
    }
  },

  // Customer specific methods
  getByEmail: async (email) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.get('/customers/email', {
        params: { email }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch customer by email';
    }
  },

  search: async (searchTerm, params = {}) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.get('/customers/search', {
        params: { search: searchTerm, ...params }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to search customers';
    }
  },

  updateLoyaltyPoints: async (id, points, reason = '') => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.post(`/customers/${id}/loyalty-points`, {
        points,
        reason
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update loyalty points';
    }
  },

  getLoyaltyHistory: async (id) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.get(`/customers/${id}/loyalty-history`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch loyalty history';
    }
  },

  updatePromoPreferences: async (id, preferences) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.put(`/customers/${id}/promo-preferences`, preferences);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update promo preferences';
    }
  },

  getTopCustomers: async (limit = 10, period = 'month') => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.get('/customers/top', {
        params: { limit, period }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch top customers';
    }
  },

  getCustomerStats: async () => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.get('/customers/stats');
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch customer statistics';
    }
  },

  exportCustomers: async (format = 'csv') => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.get('/customers/export', {
        params: { format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to export customers';
    }
  },

  bulkUpdate: async (customerIds, updateData) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.post('/customers/bulk-update', {
        customerIds,
        updateData
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to bulk update customers';
    }
  },

  mergeCustomers: async (primaryCustomerId, duplicateCustomerIds) => {
    try {
      requireAuthToken(getAccessToken);
      const response = await axiosInstance.post('/customers/merge', {
        primaryCustomerId,
        duplicateCustomerIds
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to merge customers';
    }
  }
};

export default customerAPI;